// Refer to MySPH Lightbox Documentation (WIP) for list of available service identifiers

export enum PublicationShortname {
  ST = "st",
  ZB = "zb",
  BH = "bh",
  TM = "tm",
  SM = "sm",
  BT = "bt",
  // Balar Murasu - Monthly publication managed by TM
  BM = "bm",
  // Manavar Murasu - Monthly primary school pub by TM
  MM = "mm",
  // Little Red Dot - Single Copy School pub
  LRD = "lrd",
  // ST In - Single Copy School pub
  IN = "in",
  // Gen G - Single Copy School pub
  GENG = "geng",
  TABLA = "tabla",
  // Her Wold - Magazine
  HW = "hw",
  // Nuyou - Magazine
  NY = "ny",
  // Haper's Bazaar 
  HB = "hb",
  DEFAULT = "default",
}

export enum PublicationSpecialPathName {
  TM_SINGLE = "tm-single",
  SM_SINGLE = "sm-single",
  ZB_SINGLE = "zb-single",
}

export type PublicationPathName =
  | PublicationShortname
  | PublicationSpecialPathName;

export const NO_AUTH_PUBLICATION: PublicationShortname[] = [
  PublicationShortname.TABLA,
  PublicationShortname.BM,
  PublicationShortname.MM,
];

export const NO_CALENDER_VIEW_PUBLICATION: PublicationShortname[] = [
  PublicationShortname.TABLA,
  PublicationShortname.BM,
  PublicationShortname.ZB,
  PublicationShortname.SM,
  PublicationShortname.MM,
  PublicationShortname.HW,
  PublicationShortname.NY,
  PublicationShortname.HB,
];

export const AUTH_PUBLICATION: PublicationShortname[] = Object.values(
  PublicationShortname,
).filter((pub) => {
  return !NO_AUTH_PUBLICATION.includes(pub);
});

const PREVIEW_SUFFIX = "preview";

/**
 * True for a preview publication id of the form `<base>preview` where `<base>`
 * is a known PublicationShortname (e.g. `tmpreview`, `stpreview`). Mirrors the
 * BFF's isSupportedPreviewPub (src/api/v1/preview/helpers.ts). Used by the edge
 * preventUnauthDownload carve-out: preview issues are public by design, so
 * their /ccidist-ws/* fetches must not be gated by the signed-cookie check.
 */
export function isPreviewPubId(pubId: string): boolean {
  if (!pubId.endsWith(PREVIEW_SUFFIX)) {
    return false;
  }
  const base = pubId.slice(0, -PREVIEW_SUFFIX.length);
  return (
    base.length > 0 &&
    (Object.values(PublicationShortname) as string[]).includes(base)
  );
}

type NoAuthPublication = (typeof NO_AUTH_PUBLICATION)[number];

type AuthRequiredPublication = Exclude<PublicationShortname, NoAuthPublication>;

// List of supported languages as per epaper and mysph lightbox
type SupportedLang = "en" | "zh" | "ms" | "ta";

const PUB_LANG = {
  [PublicationShortname.ST]: "en",
  [PublicationShortname.ZB]: "zh",
  [PublicationShortname.BT]: "en",
  [PublicationShortname.BH]: "ms",
  [PublicationShortname.TM]: "ta",
  [PublicationShortname.BM]: "ta",
  [PublicationShortname.MM]: "ta",
  [PublicationShortname.TABLA]: "en",
  [PublicationShortname.SM]: "zh",
  [PublicationShortname.HW]: "en",
  [PublicationShortname.NY]: "en",
  [PublicationShortname.HB]: "en",
  [PublicationShortname.DEFAULT]: "en",
} as const;

export function getLanguageByPubId(pubId: PublicationShortname): SupportedLang {
  return PUB_LANG[pubId];
}

const SERVICE_IDENTIFIER: Record<AuthRequiredPublication, string> = {
  [PublicationShortname.ST]: "stp",
  [PublicationShortname.ZB]: "zbs_ep",
  [PublicationShortname.BT]: "bts_ep",
  [PublicationShortname.BH]: "bh_online_ep",
  [PublicationShortname.TM]: "cir_tm_online",
  [PublicationShortname.SM]: "cir_sm_online",
  [PublicationShortname.DEFAULT]: "stp", // Temporary solution until we can conclude on default pubID
} as const;

export function getServiceFlagsByPubsId(
  pubsId: AuthRequiredPublication,
): string {
  return SERVICE_IDENTIFIER[pubsId];
}

export function getPubIdBySvc(svc: string): PublicationShortname | undefined {
  const keys: PublicationShortname[] = Object.keys(
    SERVICE_IDENTIFIER,
  ) as PublicationShortname[]; // Cast needed here since Object.keys doesn't retain types
  const pubId = keys.find((key) => SERVICE_IDENTIFIER[key] === svc);
  return pubId;
}

const SUBSCRIBE_NOW_URL: Record<AuthRequiredPublication, string> = {
  [PublicationShortname.ST]:
    "https://subscribe.sph.com.sg/pages/epaper/buy?utm_campaign=st_subscription&utm_medium=sph-publication&utm_source=epaper-st&utm_content=buybutton-epaperwall-st-singlecopy",
  [PublicationShortname.ZB]:
    "https://subscribe.sph.com.sg/publications-zb/?utm_campaign=zb_subscription&utm_medium=sph-publication&utm_source=epaper-zb&utm_content=subscribebutton-epaperwall-zb",
  [PublicationShortname.BT]:
    "https://subscribe.sph.com.sg/pages/epaper/buy?utm_campaign=bt_subscription&utm_medium=sph-publication&utm_source=epaper-bt&utm_content=buybutton-epaperwall-bt-singlecopy",
  [PublicationShortname.BH]:
    "https://subscribe.sph.com.sg/pages/epaper/buy?utm_campaign=bh_subscription&utm_medium=sph-publication&utm_source=epaper-bh&utm_content=buybutton-epaperwall-bh-singlecopy",
  [PublicationShortname.TM]:
    "https://subscribe.sph.com.sg/pages/epaper/buy?utm_campaign=tm_subscription&utm_medium=sph-publication&utm_source=epaper-tm&utm_content=buybutton-epaperwall-tm-singlecopy",
  [PublicationShortname.SM]:
    "https://subscribe.sph.com.sg/pages/zh/epaper/sm-zaobaosg?utm_campaign=sm_subscription&utm_medium=sph-publication&utm_source=epaper-sm&utm_content=buybutton-epaperwall-sm-singlecopy",
  [PublicationShortname.DEFAULT]:
    "https://subscribe.sph.com.sg/pages/epaper/buy?utm_campaign=st_subscription&utm_medium=sph-publication&utm_source=epaper-st&utm_content=buybutton-epaperwall-st-singlecopy",
} as const;

export function getSubscribeNowUrlByPubId(
  pubsId: AuthRequiredPublication,
): string {
  return SUBSCRIBE_NOW_URL[pubsId];
}
